import './styles.scss'

import React, { useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Map as PigeonMap } from 'pigeon-maps'
import PropTypes from 'prop-types'

import preloadImages from 'lib/utils/preload-images'
import railsContext from 'lib/selectors/rails-context'
import GeolocateButton from 'lib/components/geolocate-button'
import Close from 'lib/icons/close'
import I18n from 'lib/i18n'

import Attribution from './attribution'

import searchHereIcon from './assets/search-here-icon.svg'
import zoomInIcon from './assets/zoom-in-icon.svg'
import zoomOutIcon from './assets/zoom-out-icon.svg'

import { MAPTILER_PROD_ACCESS_TOKEN, MAPTILER_STAGING_ACCESS_TOKEN, MAPTILER_DEV_ACCESS_TOKEN } from '~/scenes/settings'

const mapTilerLocales = {
  ar: 'c82a66e2-6bfd-40cd-a9cb-91933d7d1126',
  nl: '09bec3bd-b26d-4d6e-afb7-57d451a735a4',
  en: '3196ca3f-4c0e-4b16-9756-d80cdcd22fc3',
  et: '21ca85de-3cfd-4533-8ce1-a813289dee5a',
  fr: 'afe1c010-fb23-40e6-8d3c-251fe88eaac2',
  de: '421c14c0-2c2c-4220-8573-4e9be1a49d34',
  es: 'e216b98b-c5e6-4449-8b2c-be1d73aba173'
}

const Map = ({
  objects = [],
  mouseEvents = true,
  touchEvents = true,
  withCloseButton,
  onCloseClicked,
  onSearchHereClicked,
  withSearchHereButton,
  id,
  zoomClass,
  dpr,
  width,
  minZoom,
  defaultWidth,
  height,
  top,
  zoom,
  center,
  isGeolocating,
  scrollProtect,
  geolocate,
  onBoundsChanged,
  onAnimationStart,
  onAnimationStop,
  setZoom,
  setRef,
  onClick,
  withCustomZoomIcons,
  withCustomMeta
}) => {
  const { i18nLocale, railsEnv } = useSelector(railsContext)

  let maptilerAccessToken

  if (railsEnv === 'production') {
    maptilerAccessToken = MAPTILER_PROD_ACCESS_TOKEN
  } else if (railsEnv && railsEnv.startsWith('staging')) {
    maptilerAccessToken = MAPTILER_STAGING_ACCESS_TOKEN
  } else {
    maptilerAccessToken = MAPTILER_DEV_ACCESS_TOKEN
  }

  const preloadImagesFunc = useCallback(() => {
    const finalDpr = typeof window !== 'undefined' ? window.devicePixelRatio || dpr : dpr
    const dprExtension = finalDpr >= 2 ? '@2x' : ''

    const images = [
      `/images/map/pin-green-large${dprExtension}.png`,
      `/images/map/pin-green-large-hover${dprExtension}.png`
    ]

    preloadImages(images)
  }, [dpr])

  useEffect(() => {
    preloadImagesFunc()
  }, [preloadImagesFunc])

  const mapProvider = useCallback(
    (x, y, z, dpr) => {
      const retina = dpr && dpr >= 2 ? '@2x' : ''
      const mapId = mapTilerLocales[i18nLocale || I18n.locale] || mapTilerLocales.en
      return `https://api.maptiler.com/maps/${mapId}/256/${z}/${x}/${y}${retina}.png?key=${maptilerAccessToken}`
    },
    [i18nLocale, maptilerAccessToken]
  )

  const zoomIn = () => {
    zoom < 18 && setZoom && setZoom(zoom + 1)
  }

  const zoomOut = () => {
    zoom > 1 && setZoom && setZoom(zoom - 1)
  }

  return (
    <div
      id={id}
      className={`shared-map ${top ? 'with-top' : 'without-top'}`}
      style={{ width: '100%', height: height ? `${height}px` : '100%', position: 'relative' }}
    >
      <div
        className='shared-map-wrapper'
        style={{ width: '100%', height: height ? `${height}px` : '100%', position: 'absolute', zIndex: 1 }}
      >
        <PigeonMap
          mouseEvents={mouseEvents}
          touchEvents={touchEvents}
          minZoom={minZoom}
          ref={setRef}
          center={center}
          zoom={zoom}
          width={width}
          defaultWidth={defaultWidth}
          height={height}
          provider={mapProvider}
          dprs={typeof window !== 'undefined' && window.devicePixelRatio >= 2 ? [2] : [1]}
          zoomSnap
          attribution={<Attribution />}
          attributionPrefix={false}
          onClick={onClick}
          onBoundsChanged={onBoundsChanged}
          onAnimationStart={onAnimationStart}
          onAnimationStop={onAnimationStop}
          metaWheelZoom={scrollProtect}
          twoFingerDrag={scrollProtect}
          {...(withCustomMeta && {
            metaWheelZoomWarning: I18n.t('courses.show.map.meta_wheel'),
            twoFingerDragWarning: I18n.t('courses.show.map.meta_touch')
          })}
        >
          {objects}
        </PigeonMap>
      </div>
      {geolocate && <GeolocateButton withMap isGeolocating={isGeolocating} onClick={geolocate} />}
      {setZoom && (
        <span className={`map-button map-zoom-in-button${zoomClass ? ' ' + zoomClass : ''}`} onClick={zoomIn}>
          {withCustomZoomIcons ? (
            <img className='zoom-in-icon' alt='zoom in icon' src={`${zoomInIcon}`} width={16} height={16} />
          ) : (
            '+'
          )}
        </span>
      )}
      {setZoom && (
        <span className={`map-button map-zoom-out-button${zoomClass ? ' ' + zoomClass : ''}`} onClick={zoomOut}>
          {withCustomZoomIcons ? (
            <img className='zoom-out-icon' alt='zoom out icon' src={`${zoomOutIcon}`} width={16} height={16} />
          ) : (
            '-'
          )}
        </span>
      )}
      {withSearchHereButton && (
        <button className='full-screen-map-button search-here-button' onClick={onSearchHereClicked}>
          <img className='search-here-icon' src={`${searchHereIcon}`} height='20' width='20' alt='search here icon' />
          <span className='search-here-text'>{I18n.t('react.search.map.search_here')}</span>
        </button>
      )}
      {withCloseButton && <Close className='full-screen-map-button' onClick={onCloseClicked} color='white' />}
    </div>
  )
}

Map.propTypes = {
  objects: PropTypes.array,
  center: PropTypes.array,
  zoomClass: PropTypes.string,
  id: PropTypes.string,
  dpr: PropTypes.number,
  width: PropTypes.number,
  minZoom: PropTypes.number,
  defaultWidth: PropTypes.number,
  height: PropTypes.number,
  zoom: PropTypes.number,
  onCloseClicked: PropTypes.func,
  onSearchHereClicked: PropTypes.func,
  geolocate: PropTypes.func,
  onBoundsChanged: PropTypes.func,
  onAnimationStart: PropTypes.func,
  onAnimationStop: PropTypes.func,
  setZoom: PropTypes.func,
  setRef: PropTypes.func,
  onClick: PropTypes.func,
  withSearchHereButton: PropTypes.bool,
  mouseEvents: PropTypes.bool,
  top: PropTypes.bool,
  isGeolocating: PropTypes.bool,
  scrollProtect: PropTypes.bool,
  touchEvents: PropTypes.bool,
  withCloseButton: PropTypes.bool,
  withCustomZoomIcons: PropTypes.bool,
  withCustomMeta: PropTypes.bool
}

export default React.memo(Map)
